import { useMutation } from '@tanstack/react-query';
import { Button } from '@volvo/vcdk-react';
import React, { useContext, useMemo } from 'react';

import type { IInstallableCampaign } from '@/service/vpc/types/IInstallableCampaign';
import type { SystemIconId } from '@volvo/vcdk-assets';

import useNativeAction from '@/components/remote-software/hooks/useNativeAction';
import { EnvironmentContext } from '@/contexts/environment';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { vpcService } from '@/service/vpc/vpcService';

interface StartInstallationButtonProps {
  endCustomerUnitId: number;
  unitName: string;
  aftermarketCampaignTitle: string;
  installableCampaign: IInstallableCampaign;
  buttonText: string;
  iconStart?: SystemIconId;
  iconEnd?: SystemIconId;
}

const GoToNativeButton: React.FC<StartInstallationButtonProps> = ({
  endCustomerUnitId,
  unitName,
  aftermarketCampaignTitle,
  installableCampaign,
  buttonText,
  iconEnd,
  iconStart,
}) => {
  const { addLog, isNative } = useContext(EnvironmentContext);
  const { userHasCapability } = useFleetAccount();
  const nativeAction = useNativeAction();

  // Use centralized MSAL helper for user info
  // const { getUserInfo } = useMsalHelper();

  const hasCapability = useMemo(() => {
    return endCustomerUnitId ? userHasCapability('unit.rswdl.install', endCustomerUnitId) : false;
  }, [userHasCapability, endCustomerUnitId]);

  const { mutateAsync: mutateAsyncBlesalt } = useMutation({
    mutationFn: async () =>
      vpcService.WebToNative.bleSalt({
        chassisSeries: installableCampaign.chassisSeries.trim(),
        chassisNumber: installableCampaign.chassisNumber.trim(),
      }),
  });

  const { mutateAsync: mutateAsyncBletoken } = useMutation({
    mutationFn: async () =>
      vpcService.WebToNative.bleToken({
        chassisSeries: installableCampaign.chassisSeries.trim(),
        chassisNumber: installableCampaign.chassisNumber.trim(),
      }),
  });

  const handleMobileClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!installableCampaign) {
      throw new Error('Installing campaign missing');
    }

    try {
      const blesalt = await mutateAsyncBlesalt();
      const bletoken = await mutateAsyncBletoken();

      addLog(`saltRes: ${blesalt.toString()}\ntokenRes: ${bletoken.toString()}`);

      await nativeAction.sendEventToNative('startSoftwareInstallation', {
        ...installableCampaign,
        chassisNumber: installableCampaign.chassisNumber.trim(),
        chassisSeries: installableCampaign.chassisSeries.trim(),
        unitName: unitName,
        aftermarketCampaignTitle: aftermarketCampaignTitle,
        bleSalt: blesalt.bleSalt,
        bleToken: bletoken.token?.access_token,
      });
    } catch (error) {
      console.error('[GoToNativeButton] Failed to start software installation', error);
      addLog(`Installation start failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleClick = async (e: React.MouseEvent) => {
    if (hasCapability && isNative) {
      await handleMobileClick(e);
    }
  };

  return (
    <Button
      variant="secondary"
      size="medium"
      iconStart={iconStart}
      iconEnd={iconEnd}
      onClick={handleClick}
      disabled={!hasCapability || !isNative}
    >
      {buttonText}
    </Button>
  );
};

export default GoToNativeButton;
